import React from "react";
import { useTranslation } from "react-i18next";

import styles from "./TableContainer.module.scss";

import Input from "@/components/forms/inputs/Input";
import Icons from "@/components/icons";
import CloseSmallIcon from "@/components/icons/CloseSmallIcon";
import { Button } from "@/components/ui/v2/button";

type TableContainerProps = {
  children: React.ReactNode;
  handleFilters?: () => void;
  header?: string;
  onSearchChange?: (value: string) => void;
  rightSideButtons?: React.ReactNode[];
  searchPlaceholder?: string;
  searchValue?: string | null;
  showFilter?: boolean;
  showSearch?: boolean;
  translationKey?: string;
  type?: string;
};

const TableContainer: React.FC<TableContainerProps> = ({
  children,
  handleFilters,
  header,
  onSearchChange,
  rightSideButtons,
  searchPlaceholder = "common.search",
  searchValue,
  showFilter = false,
  showSearch = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.tableContainer}>
      {(header || showSearch || showFilter || rightSideButtons) && (
        <div className={styles.header}>
          <div className={styles.leftSide}>
            {header && <h4>{header}</h4>}
            {showSearch && (
              <div className={styles.searchBarContianer}>
                <Input
                  prefix={<Icons.SearchIcon fontSize={24} />}
                  name="search"
                  value={searchValue ? searchValue : ""}
                  placeholder={t(searchPlaceholder)}
                  onChange={(e) =>
                    onSearchChange && onSearchChange(e.target.value)
                  }
                  postfix={
                    <span
                      role="button"
                      tabIndex={searchValue ? 0 : -1}
                      onClick={() => onSearchChange?.("")}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSearchChange?.(""); } }}
                      className={
                        searchValue
                          ? styles.clearIconVisible
                          : styles.clearIconHidden
                      }
                    >
                      <CloseSmallIcon fontSize={24} />
                    </span>
                  }
                />
              </div>
            )}
            {showFilter && (
              <div className={styles.filterButton}>
                <Button variant="outline" onClick={handleFilters}>
                  <Icons.FilterIcon fontSize={24} />
                  {t(`common.filters`)}
                </Button>
              </div>
            )}
          </div>
          <div className={styles.rightSide}>
            {rightSideButtons?.map((button, index) => (
              <React.Fragment key={index}>{button}</React.Fragment>
            ))}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

export default TableContainer;
